/**
 * Pronunciation Assessment Service
 * Simple free implementation based on text similarity
 */

export interface AssessmentResult {
  score: number; // 0-100
  feedback: string;
  expected: string;
  recognized: string;
  similarity: number;
  matchedWords: string[];
  unmatchedWords: string[];
}

export class AssessmentService {
  // Calculate similarity between two strings
  private calculateSimilarity(str1: string, str2: string): number {
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();

    if (s1 === s2) return 1;
    if (s1.length === 0 || s2.length === 0) return 0;

    // Use Levenshtein distance
    const matrix: number[][] = [];

    for (let i = 0; i <= s2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= s1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= s2.length; i++) {
      for (let j = 1; j <= s1.length; j++) {
        if (s2[i - 1] === s1[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    const distance = matrix[s2.length][s1.length];
    const maxLength = Math.max(s1.length, s2.length);
    return maxLength === 0 ? 1 : (maxLength - distance) / maxLength;
  }

  // Compare word by word
  private compareWords(expected: string, recognized: string): {
    matched: string[];
    unmatched: string[];
  } {
    const expectedWords = expected.toLowerCase().split(/\s+/);
    const recognizedWords = recognized.toLowerCase().split(/\s+/);

    const matched: string[] = [];
    const unmatched: string[] = [];

    expectedWords.forEach((word) => {
      if (recognizedWords.includes(word)) {
        matched.push(word);
      } else {
        // Check if similar word exists (allowing minor mistakes)
        const similarWord = recognizedWords.find((rw) =>
          this.calculateSimilarity(word, rw) > 0.7
        );
        if (similarWord) {
          matched.push(word);
        } else {
          unmatched.push(word);
        }
      }
    });

    return { matched, unmatched };
  }

  // Generate kid-friendly feedback
  private generateFeedback(score: number, _matched: string[], unmatched: string[]): string {
    if (score >= 90) {
      return '太棒了！发音非常标准！🌟 你说得很好！';
    } else if (score >= 80) {
      return '很好！继续保持！👍 大部分都对了！';
    } else if (score >= 70) {
      return '不错！再练习一下吧！💪 你在进步！';
    } else if (score >= 60) {
      if (unmatched.length > 0) {
        return `还可以！让我们再试一次吧！😊 注意这些词: ${unmatched.join(', ')}`;
      }
      return '还可以！让我们再试一次吧！😊';
    } else {
      return '没关系，让我们再试一次！我听到了你的努力！🌈';
    }
  }

  // Assess pronunciation
  assess(expected: string, recognized: string): AssessmentResult {
    // Calculate overall similarity
    const similarity = this.calculateSimilarity(expected, recognized);

    // Compare word by word
    const { matched, unmatched } = this.compareWords(expected, recognized);

    // Calculate score
    // Weight: overall similarity (50%) + word matching (50%)
    const wordMatchRatio = matched.length / (matched.length + unmatched.length);
    const score = Math.round((similarity * 50 + wordMatchRatio * 50) * 100);

    // Generate feedback
    const feedback = this.generateFeedback(score, matched, unmatched);

    return {
      score,
      feedback,
      expected,
      recognized,
      similarity,
      matchedWords: matched,
      unmatchedWords: unmatched,
    };
  }

  // Quick check if pronunciation is acceptable
  isAcceptable(expected: string, recognized: string): boolean {
    const result = this.assess(expected, recognized);
    return result.score >= 70; // 70% or above is acceptable
  }

  // Get detailed word-level feedback
  getWordFeedback(expected: string, recognized: string): {
    words: Array<{ word: string; status: 'correct' | 'wrong' | 'missing' }>;
  } {
    const expectedWords = expected.toLowerCase().split(/\s+/);
    const recognizedWords = recognized.toLowerCase().split(/\s+/);

    const words: Array<{ word: string; status: 'correct' | 'wrong' | 'missing' }> = [];

    expectedWords.forEach((word) => {
      const exactMatch = recognizedWords.includes(word);
      const similarMatch = recognizedWords.find((rw) =>
        this.calculateSimilarity(word, rw) > 0.7
      );

      if (exactMatch) {
        words.push({ word, status: 'correct' });
      } else if (similarMatch) {
        words.push({ word, status: 'correct' }); // Accept similar pronunciation
      } else {
        words.push({ word, status: 'missing' });
      }
    });

    // Check for extra words spoken
    recognizedWords.forEach((word) => {
      if (!expectedWords.includes(word) && !expectedWords.some((ew) =>
        this.calculateSimilarity(ew, word) > 0.7
      )) {
        words.push({ word, status: 'wrong' });
      }
    });

    return { words };
  }
}

// Create singleton instance
export const assessmentService = new AssessmentService();